import React, { useEffect, useState, useCallback } from 'react';
import { fetchAnalyzerHealth, reanalyzeLead, markTenantLeadsDirty } from '../services/api';

// ─── Types ──────────────────────────────────────────────────────────────

interface HealthResponse {
  driver: string;
  summarizer: string;
  is_mock: boolean;
  cron_interval_minutes: number;
  per_tick_limit: number;
  most_recent_refresh: null | {
    lead_id: number;
    lead_name: string;
    tenant_id: number;
    at: string;
    age_seconds: number;
    is_stale: boolean;
  };
  backlog_dirty: number;
  ai_transitions_24h: { total: number; by_status: Record<string, number> };
}

interface LlmLeadStatus {
  should_change?: boolean;
  value?: string | null;
  confidence?: number;
  reason?: string;
}

interface ReanalyzeResponse {
  lead_id: number;
  lead_name: string;
  tenant_id: number;
  apply?: boolean;
  channels?: string;
  turns?: Array<{ speaker: string; text: string }>;
  note?: string;
  llm?: {
    summarizer: string;
    summary: string;
    sentiment: string | null;
    next_steps: Array<{ title: string; priority: string; due_in_days: number }>;
    lead_status: LlmLeadStatus;
  };
  prediction?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown> | null;
}

// ─── Styling helpers ────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
  padding: 20, marginBottom: 16,
};
const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const value: React.CSSProperties = { fontSize: 15, color: 'var(--text)', fontWeight: 500 };
const predictionColor = (p: string): string => {
  if (p.startsWith('APPLIED')) return '#059669';
  if (p.startsWith('SUGGESTED')) return '#d97706';
  if (p.startsWith('BLOCKED')) return '#dc2626';
  return '#6b7280';
};

// ─── Page ───────────────────────────────────────────────────────────────

const AnalyzerConsolePage: React.FC = () => {
  const [health, setHealth]     = useState<HealthResponse | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [tenantFilter, setTenantFilter] = useState<string>('');

  const [leadIdInput, setLeadIdInput] = useState<string>('');
  const [running, setRunning]         = useState<boolean>(false);
  const [result, setResult]           = useState<ReanalyzeResponse | null>(null);
  const [resultErr, setResultErr]     = useState<string | null>(null);

  const [dirtyRunning, setDirtyRunning] = useState<boolean>(false);
  const [dirtyResult, setDirtyResult]   = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    setHealthErr(null);
    try {
      const t = tenantFilter ? Number(tenantFilter) : undefined;
      const h = await fetchAnalyzerHealth(t);
      setHealth(h);
    } catch (e: any) {
      setHealthErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load health');
    }
  }, [tenantFilter]);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const run = async (apply: boolean) => {
    if (!leadIdInput) return;
    setRunning(true);
    setResult(null);
    setResultErr(null);
    try {
      const r = await reanalyzeLead(Number(leadIdInput), apply);
      setResult(r);
      if (apply) loadHealth();
    } catch (e: any) {
      setResultErr(e?.response?.data?.error ?? e?.message ?? 'Failed to run analyzer');
    } finally {
      setRunning(false);
    }
  };

  const runMarkDirty = async () => {
    if (!tenantFilter) return;
    if (!window.confirm(`Mark all summarized leads in tenant ${tenantFilter} as summary_dirty=true? Cron will re-process them.`)) return;
    setDirtyRunning(true);
    setDirtyResult(null);
    try {
      const r = await markTenantLeadsDirty(Number(tenantFilter), true);
      setDirtyResult(`Marked ${r.marked_dirty} leads dirty. Drain estimate: ~${r.drain_estimate_min} min.`);
      loadHealth();
    } catch (e: any) {
      setDirtyResult('Error: ' + (e?.response?.data?.error ?? e?.message ?? 'unknown'));
    } finally {
      setDirtyRunning(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 24px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Analyzer Console</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 14 }}>
        Manually run the AI summarizer + status inference for a lead, or force the whole tenant to re-process.
        The every-minute <code>summaries:refresh</code> cron handles this automatically for <code>summary_dirty=true</code> leads;
        use this page when the cron missed something, or you want to preview LLM output before persisting.
      </p>

      {/* ── Health card ───────────────────────────────── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Cron health</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number" placeholder="Tenant id (optional)" value={tenantFilter}
              onChange={e => setTenantFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, width: 160, fontSize: 13 }}
            />
            <button className="btn" onClick={loadHealth} style={{ padding: '6px 12px', fontSize: 13 }}>
              Refresh
            </button>
          </div>
        </div>

        {healthErr && <div style={{ color: '#dc2626', fontSize: 13 }}>{healthErr}</div>}
        {!health && !healthErr && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>}

        {health && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div>
              <div style={label}>LLM binding</div>
              <div style={value}>
                {health.driver}
                {health.is_mock && <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>(MOCK)</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {health.summarizer.split('\\').pop()}
              </div>
            </div>
            <div>
              <div style={label}>Backlog</div>
              <div style={{ ...value, color: health.backlog_dirty > 500 ? '#dc2626' : 'var(--text)' }}>
                {health.backlog_dirty}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {health.per_tick_limit}/tick · every {health.cron_interval_minutes}m
              </div>
            </div>
            <div>
              <div style={label}>Last refresh</div>
              <div style={value}>
                {health.most_recent_refresh ? Math.floor((health.most_recent_refresh.age_seconds ?? 0) / 60) + 'm ago' : '—'}
              </div>
              <div style={{ fontSize: 11, color: health.most_recent_refresh?.is_stale ? '#dc2626' : 'var(--text-muted)', marginTop: 2 }}>
                {health.most_recent_refresh?.is_stale ? 'STALE — check scheduler' : 'ticking'}
              </div>
            </div>
            <div>
              <div style={label}>AI transitions 24h</div>
              <div style={value}>{health.ai_transitions_24h.total}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {Object.entries(health.ai_transitions_24h.by_status).map(([s, n]) => `${s}:${n}`).join(' · ') || '—'}
              </div>
            </div>
          </div>
        )}

        {tenantFilter && (
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16 }}>
            <button
              className="btn" onClick={runMarkDirty} disabled={dirtyRunning}
              style={{ padding: '8px 14px', fontSize: 13, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6 }}
            >
              {dirtyRunning ? 'Marking…' : `Re-process ALL summarized leads in tenant ${tenantFilter}`}
            </button>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Sets <code>summary_dirty=true</code> on every lead with an existing summary. Cron picks them up next tick.
            </div>
            {dirtyResult && <div style={{ marginTop: 8, fontSize: 13, color: dirtyResult.startsWith('Error') ? '#dc2626' : '#059669' }}>{dirtyResult}</div>}
          </div>
        )}
      </div>

      {/* ── Single lead re-analyze ─────────────────────── */}
      <div style={card}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Re-analyze a lead</h2>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={label}>Lead ID</div>
            <input
              type="number" placeholder="e.g. 1051" value={leadIdInput}
              onChange={e => setLeadIdInput(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <button
            className="btn" onClick={() => run(false)} disabled={running || !leadIdInput}
            style={{ padding: '8px 14px', fontSize: 13, background: 'var(--bg-alt)', border: '1px solid var(--border)', borderRadius: 6 }}
          >
            {running ? 'Running…' : 'Dry-run'}
          </button>
          <button
            className="btn" onClick={() => run(true)} disabled={running || !leadIdInput}
            style={{ padding: '8px 14px', fontSize: 13, background: '#059669', color: 'white', border: 'none', borderRadius: 6 }}
          >
            {running ? 'Applying…' : 'Apply'}
          </button>
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Dry-run calls the LLM and shows the JSON without persisting. Apply also runs the LeadStatusApplier + updates the lead.
        </div>

        {resultErr && <div style={{ marginTop: 12, color: '#dc2626', fontSize: 13 }}>{resultErr}</div>}
      </div>

      {/* ── Result panel ──────────────────────────────── */}
      {result && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>
              Lead {result.lead_id} · {result.lead_name} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>· tenant {result.tenant_id}</span>
            </h2>
            {result.prediction && (
              <span style={{ padding: '4px 10px', borderRadius: 12, background: '#f3f4f6', color: predictionColor(result.prediction), fontSize: 12, fontWeight: 600 }}>
                {result.prediction}
              </span>
            )}
          </div>

          {result.note && (
            <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, color: '#92400e', fontSize: 13, marginBottom: 12 }}>
              {result.note}
            </div>
          )}

          {result.llm && (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={label}>LLM Summary</div>
                <div style={{ ...value, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {result.llm.summary || <em style={{ color: 'var(--text-muted)' }}>(empty)</em>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={label}>Sentiment</div>
                  <div style={value}>{result.llm.sentiment ?? '—'}</div>
                </div>
                <div>
                  <div style={label}>should_change</div>
                  <div style={{ ...value, color: result.llm.lead_status?.should_change ? '#059669' : '#6b7280' }}>
                    {result.llm.lead_status?.should_change ? 'TRUE' : 'false'}
                  </div>
                </div>
                <div>
                  <div style={label}>Proposed status</div>
                  <div style={value}>{result.llm.lead_status?.value ?? '—'}</div>
                </div>
                <div>
                  <div style={label}>Confidence</div>
                  <div style={{ ...value, color: (result.llm.lead_status?.confidence ?? 0) >= 0.7 ? '#059669' : '#d97706' }}>
                    {result.llm.lead_status?.confidence?.toFixed(2) ?? '—'}
                  </div>
                </div>
              </div>

              {result.llm.lead_status?.reason && (
                <div style={{ marginBottom: 14 }}>
                  <div style={label}>Reason</div>
                  <div style={value}>{result.llm.lead_status.reason}</div>
                </div>
              )}

              {result.llm.next_steps.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={label}>Next steps ({result.llm.next_steps.length})</div>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {result.llm.next_steps.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: 'var(--text)' }}>
                        {s.title} · <span style={{ color: 'var(--text-muted)' }}>{s.priority} · due in {s.due_in_days}d</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {result.after && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 14 }}>
              <div style={label}>Post-apply</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                lead_status = <b>{(result.after as any).lead_status ?? '—'}</b> (src={(result.after as any).lead_status_source ?? '—'})<br />
                refreshed at {(result.after as any).summary_refreshed_at ?? '—'}
              </div>
            </div>
          )}

          {result.turns && result.turns.length > 0 && (
            <details style={{ marginTop: 14 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                Turns fed to LLM ({result.turns.length})
              </summary>
              <div style={{ marginTop: 10, background: 'var(--bg-alt)', padding: 12, borderRadius: 6, fontSize: 12, fontFamily: 'monospace', maxHeight: 300, overflowY: 'auto' }}>
                {result.turns.map((t, i) => (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ color: '#7c3aed', fontWeight: 600 }}>[{i}] {t.speaker}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{t.text}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyzerConsolePage;
